import { useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import StatValue from './StatValue';

// ADVANCED stats tab, in the same card style as CopilotDoubleStatsGrid.
// The tile body depends on `stat.kind` (see ADVANCED_STAT_CAPTIONS).

const CARD_BG = '#2b2b2b';
const TEXT = '#ffffff';
const TEXT_SHADOW = '0 1px 4px rgba(0,0,0,0.7)';
const FALLBACK_HOME = '#2f6fd0';
const FALLBACK_AWAY = '#3aa564';
const LIVE = '#feec67';
const COLS = 5;

const ValueBox = ({ value, label, height, fontSize, labelSize }) => (
  <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
    <StatValue value={value} maxPx={fontSize} width="92%" height={height} />
    {label && (
      <Typography sx={{ fontSize: labelSize, fontWeight: 700, textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)', lineHeight: 1 }}>
        {label}
      </Typography>
    )}
  </Box>
);

ValueBox.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string,
  height: PropTypes.number,
  fontSize: PropTypes.number,
  labelSize: PropTypes.number,
};

const TileBody = ({ stat, s }) => {
  const box = { height: s(44), fontSize: s(30), labelSize: s(12) };
  if (stat.kind === 'twoWay') {
    // Same values as the Single stats tiles: one large number per team half.
    return (
      <Box sx={{ display: 'flex' }}>
        {[stat.home, stat.away].map((val, vi) => (
          <Box key={vi} sx={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'center', overflow: 'hidden' }}>
            <StatValue value={val} maxPx={s(52)} width="86%" height={s(56)} />
          </Box>
        ))}
      </Box>
    );
  }
  if (stat.kind === 'threeWay') {
    return (
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {stat.values.map((v, i) => <ValueBox key={i} value={v} label={stat.labels[i]} {...box} />)}
      </Box>
    );
  }
  return null;
};

TileBody.propTypes = {
  stat: PropTypes.object.isRequired,
  s: PropTypes.func.isRequired,
};

// Title size per tile kind: name-only tiles match the shortcut buttons above the grid,
// two-way matches the Single stats tiles, three-way fills the gap above its values.
const TITLE_PX = { title: 26, twoWay: 17, threeWay: 22 };

export default function CopilotAdvancedStatsGrid({ stats = [], leftColor, rightColor, activeKey, onStatClick, scale = 1 }) {
  const home = leftColor || FALLBACK_HOME;
  const away = rightColor || FALLBACK_AWAY;
  const s = (px) => Math.round(px * scale);
  const gridRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const tabIdx = Math.min(activeIdx, Math.max(0, stats.length - 1));

  const handleKeyDown = (e) => {
    if (!['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].includes(e.key)) return;
    const btns = Array.from(gridRef.current?.querySelectorAll('[data-stat-btn]') ?? []);
    const idx = btns.indexOf(document.activeElement);
    if (idx === -1) return;
    e.preventDefault();
    e.stopPropagation();
    const delta = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowDown' ? COLS : -COLS;
    const next = Math.max(0, Math.min(btns.length - 1, idx + delta));
    setActiveIdx(next);
    btns[next]?.focus();
  };

  return (
    <Box
      ref={gridRef}
      onKeyDown={handleKeyDown}
      sx={{
        // 8px past the panel on the sides and bottom: room for the tiles'
        // rings, while the tiles themselves line up with the column edges.
        height: 'calc(100% + 8px)',
        width: 'calc(100% + 16px)',
        overflowY: 'auto',
        display: 'grid',
        gridTemplateColumns: `repeat(${COLS}, 1fr)`,
        // Three rows share the height the panel gives, so they fit without scrolling.
        gridTemplateRows: 'repeat(3, minmax(0, 1fr))',
        gridAutoRows: `${s(156)}px`,
        gap: 1.5,
        px: 1,
        mx: -1,
        mb: -1,
        pt: 0.75,
        pb: 1,
        alignContent: 'start',
      }}
    >
      {stats.map((stat, i) => {
        const isLive = activeKey && stat.key === activeKey;
        const titleOnly = stat.kind === 'title';
        const twoWay = stat.kind === 'twoWay';
        return (
          <Box
            key={stat.key}
            component="button"
            data-stat-btn="true"
            tabIndex={i === tabIdx ? 0 : -1}
            onFocus={() => setActiveIdx(i)}
            onClick={() => onStatClick?.(stat)}
            className="no-drag"
            sx={{
              position: 'relative',
              display: 'flex',
              p: 0,
              bgcolor: 'transparent',
              border: 'none',
              borderRadius: 2,
              cursor: 'pointer',
              transition: 'box-shadow 0.15s ease, transform 0.1s ease',
              outline: 'none',
              boxShadow: isLive ? `0 0 0 3px ${LIVE}, 0 0 14px rgba(254,236,103,0.5)` : 'none',
              '&:hover': { boxShadow: isLive ? `0 0 0 3px ${LIVE}, 0 0 14px rgba(254,236,103,0.5)` : `0 0 0 2px ${LIVE}` },
              '&:focus-visible': { boxShadow: `0 0 0 2px ${LIVE}` },
              '&:active': { transform: 'scale(0.99)' },
            }}
          >
            <Box sx={{ flex: 1, bgcolor: CARD_BG, borderTop: titleOnly ? 'none' : `4px solid ${home}`, borderRadius: '8px 0 0 8px' }} />
            <Box sx={{ flex: 1, bgcolor: CARD_BG, borderTop: titleOnly ? 'none' : `4px solid ${away}`, borderRadius: '0 8px 8px 0' }} />

            {isLive && (
              <Box sx={{ position: 'absolute', top: 8, right: 8, width: 11, height: 11, borderRadius: '50%', bgcolor: '#22c55e', boxShadow: '0 0 8px #22c55e', zIndex: 2 }} />
            )}

            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', justifyContent: titleOnly ? 'center' : 'space-between', gap: twoWay ? 1 : 0.75, px: 1, py: twoWay ? 1.5 : 0.75 }}>
              <Typography
                sx={{
                  textAlign: 'center',
                  fontSize: s(TITLE_PX[stat.kind] ?? TITLE_PX.title),
                  fontWeight: 700,
                  letterSpacing: twoWay ? '0.04em' : 0.3,
                  textTransform: 'uppercase',
                  lineHeight: twoWay ? 1.15 : 1.05,
                  color: TEXT,
                  textShadow: TEXT_SHADOW,
                }}
              >
                {stat.title}
              </Typography>
              <TileBody stat={stat} s={s} />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

CopilotAdvancedStatsGrid.propTypes = {
  stats: PropTypes.array,
  leftColor: PropTypes.string,
  rightColor: PropTypes.string,
  activeKey: PropTypes.string,
  onStatClick: PropTypes.func,
  scale: PropTypes.number,
};
